import { CompanyMetadataItem } from '@aibees/elephant/types';
import { IconType } from '@aibees/shared/components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { COMPANY_ID_FILTER_SEPARATOR } from '@aibees/elephant/constants/filters';
import { Stack, Typography } from '@mui/material';
import {
  companySearchIdsLabelParser,
  companySearchLabelParser,
  getDomainIfExist,
} from '../components/filter-tags/utils';
import {
  FilterBadgeType,
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';
import { companySearchApiResponseMapper } from '../utils';
import { companyBulkSearchItems } from './company-search';

export default function peopleCompanySearchFilterConfig(): SearchFilterBase {
  const currentCompany: SearchFilter = {
    name: 'Current Company',
    type: SearchFilterType.NONE,
    icon: undefined,
    index: 0,
    key: 'current_company',
    field: 'current_company',
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Current Company',
        type: SearchFilterType.NONE,
        icon: undefined,
        index: 0,
        key: 'current',
        field: 'current',
        metadata: { color: 'primary', singleEntry: false },
        config: { labelKey: 'label', valueKey: 'value' },
        nested: true,
        open: true,
        utils: {
          tags: {
            config: {
              showAsCard: true,
              tagsCardTitle: 'Current Company',
            },
          },
        },
        sections: { main: 3, more: 0 },
        items: [
          {
            name: 'Primary active company only',
            type: SearchFilterType.SWITCH,
            index: 0,
            layer: 4,
            key: 'latest_active_company_only',
            field: 'latest_active_company_only',
            metadata: {
              color: 'primary',
              info: latestActiveCompanyOnlyInfo(),
              hideTags: true,
            },
          },
          {
            name: 'Include',
            type: SearchFilterType.CUSTOM_SELECT,
            index: 1,
            layer: 4,
            field: 'include_current_company',
            key: 'include_current_company',
            metadata: {
              color: 'primary',
              icon: 'add-circle-outline',
              preset: 'company',
              placeholder: 'Enter Name Company (OR) Search',
              autocomplete: {
                recent: false,
                transformSearchValue: getDomainIfExist,
                multiple: true,
                checkboxPosition: 'right',
                avartarFallbackIcon: 'business',
              },
              delimiter: '::',
              limit: {
                max: {
                  size: 100,
                  message:
                    'Manual company search entry are limited to 100 records, please use the bulk Include/exclude to add more.',
                },
              },
            },
            searchKey: 'search',
            config: { labelKey: 'label', valueKey: 'value' },
            utils: {
              optionsApiFormatter: (
                data?: CompanyMetadataItem[],
                icon?: IconType,
                delimiter?: string
              ) => companySearchApiResponseMapper(data, icon, delimiter),
              tags: { labelFormatter: companySearchLabelParser },
            },
            options: [
              {
                type: 'URL',
                value: `/elephant-management/um/workspaces/:wid/companies/metadata-management?return=list&page=0&size=10&employee_count=desc`,
                label: '',
                icon: null,
                index: undefined,
              },
            ],
          },
          {
            name: 'Exclude',
            type: SearchFilterType.CUSTOM_SELECT,
            index: 2,
            layer: 4,
            key: 'exclude_current_company',
            field: 'exclude_current_company',
            metadata: {
              color: 'error',
              icon: 'block',
              preset: 'company',
              placeholder: 'Enter Name Company (OR) Search',
              autocomplete: {
                recent: false,
                transformSearchValue: getDomainIfExist,
                multiple: true,
                checkboxPosition: 'right',
                avartarFallbackIcon: 'business',
              },
              delimiter: '::',
              limit: {
                max: {
                  size: 100,
                  message:
                    'Manual company search entry are limited to 100 records, please use the bulk Include/exclude to add more.',
                },
              },
            },
            searchKey: 'search',
            config: { labelKey: 'label', valueKey: 'value' },
            utils: {
              optionsApiFormatter: (
                data?: CompanyMetadataItem[],
                icon?: IconType,
                delimiter?: string
              ) => companySearchApiResponseMapper(data, icon, delimiter),
              tags: { labelFormatter: companySearchLabelParser },
            },
            options: [
              {
                type: 'URL',
                value: `/elephant-management/um/workspaces/:wid/companies/metadata-management?return=list&page=0&size=10&employee_count=desc`,
                label: '',
                icon: null,
                index: undefined,
              },
            ],
          },
        ],
      },
      {
        name: 'Bulk Manual Search',
        type: SearchFilterType.NONE,
        icon: undefined,
        index: 0,
        key: '',
        field: '',
        items: companyBulkSearchItems,
      },
    ],
  };

  const pastCompany: SearchFilter = {
    name: 'Past Company',
    type: SearchFilterType.NONE,
    icon: undefined,
    index: 0,
    key: 'past_company',
    field: 'past_company',
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Past Company',
        type: SearchFilterType.NONE,
        icon: undefined,
        index: 0,
        key: 'previous',
        field: 'previous',
        metadata: { color: 'primary', singleEntry: false },
        config: { labelKey: 'label', valueKey: 'value' },
        utils: {
          tags: {
            config: {
              showAsCard: true,
              tagsCardTitle: 'Past Company',
            },
          },
        },
        nested: true,
        open: true,
        sections: { main: 3, more: 0 },
        items: [
          {
            name: 'Include',
            type: SearchFilterType.CUSTOM_SELECT,
            index: 1,
            layer: 4,
            field: 'include_previous_company',
            key: 'include_previous_company',
            metadata: {
              color: 'primary',
              icon: 'add-circle-outline',
              preset: 'company',
              placeholder: 'Enter Name Company (OR) Search',
              autocomplete: {
                recent: false,
                transformSearchValue: getDomainIfExist,
                multiple: true,
                checkboxPosition: 'right',
                avartarFallbackIcon: 'business',
              },
              delimiter: '::',
              limit: {
                max: {
                  size: 100,
                  message:
                    'Manual company search entry are limited to 100 records, please use the bulk Include/exclude to add more.',
                },
              },
            },
            searchKey: 'search',
            config: { labelKey: 'label', valueKey: 'value' },
            utils: {
              optionsApiFormatter: (
                data?: CompanyMetadataItem[],
                icon?: IconType,
                delimiter?: string
              ) => companySearchApiResponseMapper(data, icon, delimiter),
              tags: { labelFormatter: companySearchLabelParser },
            },
            options: [
              {
                type: 'URL',
                value: `/elephant-management/um/workspaces/:wid/companies/metadata-management?return=list&page=0&size=10&employee_count=desc`,
                label: '',
                icon: null,
                index: undefined,
              },
            ],
          },
          {
            name: 'Exclude',
            type: SearchFilterType.CUSTOM_SELECT,
            index: 2,
            layer: 4,
            key: 'exclude_previous_company',
            field: 'exclude_previous_company',
            metadata: {
              color: 'error',
              icon: 'block',
              preset: 'company',
              placeholder: 'Enter Name Company (OR) Search',
              autocomplete: {
                recent: false,
                transformSearchValue: getDomainIfExist,
                multiple: true,
                checkboxPosition: 'right',
                avartarFallbackIcon: 'business',
              },
              delimiter: '::',
              limit: {
                max: {
                  size: 100,
                  message:
                    'Manual company search entry are limited to 100 records, please use the bulk Include/exclude to add more.',
                },
              },
            },
            searchKey: 'search',
            config: { labelKey: 'label', valueKey: 'value' },
            utils: {
              optionsApiFormatter: (
                data?: CompanyMetadataItem[],
                icon?: IconType,
                delimiter?: string
              ) => companySearchApiResponseMapper(data, icon, delimiter),
              tags: { labelFormatter: companySearchLabelParser },
            },
            options: [
              {
                type: 'URL',
                value: `/elephant-management/um/workspaces/:wid/companies/metadata-management?return=list&page=0&size=10&employee_count=desc`,
                label: '',
                icon: null,
                index: undefined,
              },
            ],
          },
        ],
      },
    ],
  };

  return {
    id: 'Current & Past Company',
    name: 'Current & Past Company',
    icon: 'search',
    index: 17,
    layer: 3,
    type: SearchFilterType.ROOT,
    category: [FilterCategory.COMPANY_DATA],
    pinned: true,
    badge: FilterBadgeType.BETA,
    items: [
      {
        name: '',
        type: SearchFilterType.BUTTON_GROUP,
        icon: undefined,
        index: 0,
        key: '',
        field: '',
        metadata: { allowParallelItems: true },
        items: [
          {
            name: 'Selected Companies',
            type: SearchFilterType.NONE,
            icon: undefined,
            index: -1,
            key: '',
            field: 'company_ids',
            utils: {
              tags: {
                config: {
                  showAsCard: true,
                  tagsCardTitle: 'Find by companies',
                },
              },
            },
            items: [
              {
                type: SearchFilterType.COMPANY_IDS,
                icon: 'people',
                index: 1,
                field: 'people_company_search_list_id',
                key: 'people_company_search_list_id',
                name: 'Selected Companies',
                metadata: {
                  color: 'primary',
                  delimiter: COMPANY_ID_FILTER_SEPARATOR,
                },
                utils: {
                  tags: {
                    labelFormatter: companySearchIdsLabelParser,
                  },
                },
              },
            ],
          },
          ...[
            {
              name: 'Applied Filters',
              type: SearchFilterType.APPLIED_FILTERS,
              index: 2,
              icon: undefined,
              key: 'companies_applied_filters',
              field: 'companies_applied_filters',
              metadata: {},
            },
          ],
          currentCompany,
          pastCompany,
        ],
      },
    ],
  };
}

export function latestActiveCompanyOnlyInfo() {
  return (
    <Stack direction="column" gap={1} sx={{ maxWidth: (p) => p.spacing(25) }}>
      <Typography variant="body5">
        Only matches if the company is the most recent active entry on the
        profile.
      </Typography>
      <Typography variant="body5">
        This excludes profiles where the company is listed as a secondary or
        concurrent workplace.
      </Typography>
    </Stack>
  );
}
